import {Container} from'@/ui';
import {motion} from'framer-motion';
import type {LucideIcon} from'lucide-react';

// Module-level constants to prevent re-renders
const CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    }
  }
} as const;

const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1] as const,
    }
  }
} as const;

const PRIMARY_ORB_STYLE = {
  background:'radial-gradient(circle, hsl(var(--primary) / 0.4) 0%, transparent 70%)',
} as const;

const ACCENT_ORB_STYLE = {
  background:'radial-gradient(circle, hsl(var(--accent) / 0.4) 0%, transparent 70%)',
} as const;

const GLOW_STYLES = {
  rose:'linear-gradient(180deg, hsl(var(--primary) / 0.5) 0%, transparent 50%)',
  violet:'linear-gradient(180deg, hsl(var(--accent) / 0.5) 0%, transparent 50%)',
  emerald:'linear-gradient(180deg, hsl(var(--secondary) / 0.5) 0%, transparent 50%)',
} as const;

const CORNER_GLOW_STYLES = {
  rose:'radial-gradient(circle, hsl(var(--primary)) 0%, transparent 70%)',
  violet:'radial-gradient(circle, hsl(var(--accent)) 0%, transparent 70%)',
  emerald:'radial-gradient(circle, hsl(var(--secondary)) 0%, transparent 70%)',
} as const;

export interface TransformFeature {
  number: string;
  title: string;
  description: string;
  icon: LucideIcon;
  gradient: string;
  iconGradient: string;
  borderGradient: string;
  glowColor: 'rose' | 'violet' | 'emerald';
}

export interface TransformProps {
  intro: string;
  titleLine1: string;
  titleLine2: string;
  features: TransformFeature[];
}

export const Transform = ({
  intro,
  titleLine1,
  titleLine2,
  features,
}: TransformProps): React.ReactElement => {
  return (
    <section className="py-20 sm:py-28 lg:py-36 relative overflow-hidden bg-background">

      {/* Gradient orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-0 left-1/4 w-full max-w-container-md aspect-square opacity-30 blur-3xl"
          style={PRIMARY_ORB_STYLE}
        />
        <div
          className="absolute bottom-0 right-1/4 w-full max-w-container-sm aspect-square opacity-25 blur-3xl"
          style={ACCENT_ORB_STYLE}
        />
      </div>

      <Container size="6xl"className="relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin:"-100px"}}
          variants={CONTAINER_VARIANTS}
          className="text-center mb-16 sm:mb-20 lg:mb-24"
        >
          <motion.div variants={ITEM_VARIANTS} className="mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-body-sm font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"/>
              {intro}
            </span>
          </motion.div>
          <motion.h2
            variants={ITEM_VARIANTS}
            className="text-heading-1 sm:text-display-3 lg:text-display-2 font-light text-foreground"
          >
            {titleLine1}
            <br />
            <span className="font-normal bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
              {titleLine2}
            </span>
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin:"-50px"}}
          variants={CONTAINER_VARIANTS}
          className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6"
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                variants={ITEM_VARIANTS}
                className="group relative"
              >
                {/* Animated gradient border */}
                <div className="absolute -inset-border-1 rounded-3xl bg-gradient-to-b opacity-0 group-hover:opacity-100 active:opacity-100 transition-opacity duration-500 blur-sm"
                  style={{ background: GLOW_STYLES[feature.glowColor] }}
                />

                {/* Card */}
                <div className="relative h-full p-6 sm:p-8 rounded-3xl liquid-glass border-[hsl(var(--liquid-glass-border-medium))] shadow-liquid-glass overflow-hidden transition-all duration-[var(--duration-morph)] ease-[var(--ease-liquid)] group-hover:border-[hsl(var(--liquid-glass-border-specular))] active:border-[hsl(var(--liquid-glass-border-specular))] group-hover:shadow-liquid-glass-lg">
                  {/* Gradient overlay on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                  {/* Corner glow */}
                  <div
                    className="absolute -top-20 -right-20 w-40 h-40 opacity-0 group-hover:opacity-60 active:opacity-60 transition-opacity duration-700 blur-3xl"
                    style={{ background: CORNER_GLOW_STYLES[feature.glowColor] }}
                  />

                  {/* Content */}
                  <div className="relative z-10">
                    {/* Icon with gradient background */}
                    <div className="mb-6 inline-flex">
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.iconGradient} p-border-1 shadow-lg`}>
                        <div className="w-full h-full rounded-2xl liquid-glass-thin flex items-center justify-center group-hover:bg-transparent active:bg-transparent transition-all duration-[var(--duration-morph)] ease-[var(--ease-liquid)]">
                          <Icon className="w-6 h-6 text-foreground group-hover:text-inverse-foreground active:text-inverse-foreground transition-colors duration-300"strokeWidth={1.5} />
                        </div>
                      </div>
                    </div>

                    {/* Number badge */}
                    <div className="absolute top-6 right-6 sm:top-8 sm:right-8">
                      <span className="text-body-sm font-mono text-muted-foreground/50 group-hover:text-muted-foreground active:text-muted-foreground transition-colors">
                        {feature.number}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-heading-3 sm:text-heading-2 font-semibold text-foreground mb-3 sm:mb-4">
                      {feature.title}
                    </h3>

                    {/* Description */}
                    <p className="text-body text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>

                    {/* Learn more link */}
                    <div className="mt-6 flex items-center gap-2 text-body-sm font-medium text-foreground/60 group-hover:text-foreground active:text-foreground transition-colors">
                      <span>Learn more</span>
                      <svg
                        className="w-4 h-4 transform group-hover:translate-x-1 active:translate-x-1 transition-transform"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </Container>

      {/* Add gradient animation keyframes via style tag */}
      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          animation: gradient 6s ease infinite;
        }
      `}</style>
    </section>
  );
};
